// User-defined attributes: merging the resolved attribute columns into a form.
//
// The client never gets attribute columns from the model endpoint (plan D4):
// the `uda_resolve` read action hands out the ColumnDefs of the sets that
// APPLY, and the screen merges them into the form's `sm` here — so every
// renderer, editor, item change and `{form.X}` token treats an attribute as
// the ordinary column it is. Pure functions; the input model is never mutated,
// and no columns ⇒ the very same `sm` object (screens without attributes
// render byte-identically and React memo identities never move).

import type { ColumnDef, SqlModel } from '../txn/types'
import type { UdaColumnDef, UdaFlag } from './types'

function formKeyOf(c: { name?: string; db_name?: string }): string {
  // Same rule as txn/utils columnFormKey (kept local: utils is heavy and this
  // module is imported by pure node tests).
  const raw = String(c.name || c.db_name || '').trim()
  return raw.replace(/\s+/g, '_').toLowerCase()
}

export function isUdaColumn(c: unknown): c is UdaColumnDef {
  return !!c && typeof c === 'object' && !!(c as { uda?: unknown }).uda
    && typeof (c as { uda?: unknown }).uda === 'object'
}

export function isDriverColumn(c: unknown): boolean {
  return isUdaColumn(c) && !!c.uda.driver
}

/** The attribute key of an injected column (`colour` for `uda_colour`). */
export function udaKeyOf(c: ColumnDef): string | null {
  return isUdaColumn(c) && !c.uda.driver ? String(c.uda.key || '') || null : null
}

const truthy = (v: unknown): boolean => v === true || v === 1
  || (typeof v === 'string' && ['1', 'true', 'yes', 'y'].includes(v.trim().toLowerCase()))

/** Columns promoted to grids (`in_grid`), attributes only. */
export function promotedColumns(columns: readonly UdaColumnDef[] | null | undefined): UdaColumnDef[] {
  return (columns || []).filter(c => isUdaColumn(c) && !c.uda.driver && truthy(c.in_grid))
}

// Memo: one merged model per (form, column set, applicable keys).
const memo = new WeakMap<SqlModel, Map<string, SqlModel>>()

function signature(columns: readonly UdaColumnDef[], keys: readonly string[] | null | undefined): string {
  const parts = columns.map(c => `${formKeyOf(c)}@${c.uda?.def_version ?? 0}`)
  return `${parts.join(',')}#${keys ? keys.join(',') : '*'}`
}

/**
 * `sm` with the injected attribute columns appended.
 *
 * - `applicableKeys` (attribute keys, `uda.key`) keeps only the columns that
 *   apply to ONE record or row; deciding-value columns are always kept (they
 *   are hidden). Omitted ⇒ every given column.
 * - A column the form already has (same form key) is skipped, never doubled.
 * - Placement: a column without its own `x` / `y` is put AFTER the form's own
 *   columns (grids order by x, forms order groups by their first y), so the
 *   additional details read after the standard ones, as in the prototype.
 *
 * Memoised by the form object + the column set (def_version included), so the
 * same answer yields the same model object render after render.
 */
export function withUdaColumns(
  sm: SqlModel, columns: readonly UdaColumnDef[] | null | undefined,
  applicableKeys?: readonly string[] | null,
): SqlModel
export function withUdaColumns(
  sm: SqlModel | null | undefined, columns: readonly UdaColumnDef[] | null | undefined,
  applicableKeys?: readonly string[] | null,
): SqlModel | null | undefined
export function withUdaColumns(
  sm: SqlModel | null | undefined, columns: readonly UdaColumnDef[] | null | undefined,
  applicableKeys?: readonly string[] | null,
): SqlModel | null | undefined {
  if (!sm || !columns || columns.length === 0) return sm
  const sig = signature(columns, applicableKeys)
  let byForm = memo.get(sm)
  const hit = byForm?.get(sig)
  if (hit) return hit
  const own = new Set((sm.columns || []).map(w => (w?.column ? formKeyOf(w.column) : '')).filter(Boolean))
  const keep = applicableKeys ? new Set(applicableKeys) : null
  const added: ColumnDef[] = []
  const seen = new Set<string>()
  for (const c of columns) {
    if (!isUdaColumn(c)) continue
    const fk = formKeyOf(c)
    if (!fk || own.has(fk) || seen.has(fk)) continue
    if (keep && !c.uda.driver && !keep.has(String(c.uda.key))) continue
    seen.add(fk)
    added.push(c)
  }
  if (added.length === 0) return cache(sm, sig, sm)
  let maxX = 0
  let maxY = 0
  for (const w of sm.columns || []) {
    const x = Number(w?.column?.x)
    const y = Number(w?.column?.y)
    if (Number.isFinite(x)) maxX = Math.max(maxX, x)
    if (Number.isFinite(y)) maxY = Math.max(maxY, y)
  }
  const placed = added.map((c, i) => {
    const hasX = String(c.x ?? '').trim() !== ''
    const hasY = String(c.y ?? '').trim() !== ''
    if (hasX && hasY) return { column: c }
    return {
      column: {
        ...c,
        ...(hasX ? {} : { x: String(maxX + 1 + i) }),
        ...(hasY ? {} : { y: String(maxY + 1 + i) }),
      } as ColumnDef,
    }
  })
  const out: SqlModel = { ...sm, columns: [...(sm.columns || []), ...placed] }
  return cache(sm, sig, out)

  function cache(key: SqlModel, s: string, value: SqlModel): SqlModel {
    if (!byForm) { byForm = new Map(); memo.set(key, byForm) }
    // Bounded per form: a long session on one screen must not grow this.
    if (byForm.size > 64) byForm.clear()
    byForm.set(s, value)
    return value
  }
}

// ── the Details chip ────────────────────────────────────────────────────────

export interface UdaDetailsCounts {
  /** Applicable attributes with a value. */
  filled: number
  /** Applicable attributes (visible ones; deciding values excluded). */
  total: number
  /** Applicable mandatory attributes without a value. */
  missing: number
}

const blank = (v: unknown): boolean => v === null || v === undefined
  || (typeof v === 'string' && v.trim() === '')
  || (Array.isArray(v) && v.length === 0)

/** `filled / total · N required missing` for one record or row, counted over
 *  the attributes that APPLY to it (`attrKeys`; omitted ⇒ every column). */
export function detailsCounts(
  columns: readonly UdaColumnDef[] | null | undefined,
  values: Record<string, unknown> | null | undefined,
  attrKeys?: readonly string[] | null,
): UdaDetailsCounts {
  const keep = attrKeys ? new Set(attrKeys) : null
  const v = values || {}
  let filled = 0
  let total = 0
  let missing = 0
  for (const c of columns || []) {
    if (!isUdaColumn(c) || c.uda.driver || truthy(c.hidden)) continue
    if (keep && !keep.has(String(c.uda.key))) continue
    total += 1
    const empty = blank(v[formKeyOf(c)])
    if (!empty) filled += 1
    else if (truthy(c.mandatory)) missing += 1
  }
  return { filled, total, missing }
}

/** The chip's text: `2/3 · 1 required missing` (prototype `dchip`). */
export function detailsChipText(c: UdaDetailsCounts): string {
  return `${c.filled}/${c.total}${c.missing ? ` · ${c.missing} required missing` : ''}`
}

/** Whether a promoted grid cell applies to its row: `attrKeys` of that row
 *  hold the attribute. A row whose resolution is not known yet (null) is
 *  treated as applying, so a cell is never wrongly locked as n/a. */
export function cellApplies(col: ColumnDef, attrKeys: readonly string[] | null | undefined): boolean {
  const key = udaKeyOf(col)
  if (!key) return true
  if (!attrKeys) return true
  return attrKeys.includes(key)
}

/** The deciding values of a resolution as the virtual `uda_drv_<key>` form
 *  keys (rules and flags read `{form.uda_drv_cust_type}`). */
export function driverValues(
  columns: readonly UdaColumnDef[] | null | undefined,
  drivers: Record<string, string | null> | null | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (!drivers) return out
  for (const c of columns || []) {
    if (!isUdaColumn(c) || !c.uda.driver) continue
    const k = String(c.uda.key || '')
    if (k in drivers) out[formKeyOf(c)] = drivers[k]
  }
  return out
}

/** The plain-language "why" for a row: `Customer type = EXPORT · Item type =
 *  APPAREL`, from the deciding-value columns' headings (prototype whyText). */
export function driversSentence(
  columns: readonly UdaColumnDef[] | null | undefined,
  drivers: Record<string, string | null> | null | undefined,
): string {
  if (!drivers) return ''
  const parts: string[] = []
  for (const c of columns || []) {
    if (!isUdaColumn(c) || !c.uda.driver) continue
    const k = String(c.uda.key || '')
    const v = drivers[k]
    if (v === null || v === undefined || v === '') continue
    parts.push(`${c.heading || k} = ${v}`)
  }
  return parts.join(' · ')
}

/** Flags that show in one place (`header_badge` / `list_column` / `line_row`). */
export function flagsFor(flags: readonly UdaFlag[] | null | undefined, place: string): UdaFlag[] {
  return (flags || []).filter(f => !f.hidden && (f.show_on || []).includes(place))
}

/** The sentence the engine gives a set asked of every record (mirrors
 *  `resolve.why` in twasta_runtime_engine/uda/resolve.py). */
export const UDA_WHY_EVERY_RECORD = 'Asked for every record'

/** A set's caption: "Asked because Customer type is EXPORT", or the engine's
 *  every-record sentence as it stands. */
export function whyCaption(why: string | null | undefined): string {
  const w = String(why || '').trim()
  if (!w) return ''
  return w === UDA_WHY_EVERY_RECORD ? w : `Asked because ${w}`
}

/** Captions per form group for the record's applicable sets (record scope).
 *  A group that also holds the form's OWN columns gets none — the caption
 *  would wrongly explain the standard fields too. */
export function groupCaptions(
  sets: ReadonlyArray<{ group?: string | null; label?: string; why?: string }> | null | undefined,
  baseGroups: ReadonlySet<string>,
  /** Appended to a reason ("(from the customer master)"). */
  suffix?: (why: string) => string,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const s of sets || []) {
    const g = String(s.group || s.label || '').trim()
    const base = whyCaption(s.why)
    const extra = base && base !== UDA_WHY_EVERY_RECORD && suffix ? suffix(String(s.why || '')) : ''
    const text = extra ? `${base} ${extra}` : base
    if (!g || !text || baseGroups.has(g)) continue
    out[g] = out[g] && !out[g].includes(text) ? `${out[g]}; ${text}` : (out[g] || text)
  }
  return out
}

/** The attribute groups of a merged form that hold NO standard column — the
 *  groups the "Additional details" sub-heading goes above. */
export function attributeGroups(
  columns: readonly UdaColumnDef[] | null | undefined, baseGroups: ReadonlySet<string>,
): string[] {
  const out: string[] = []
  for (const c of columns || []) {
    if (!isUdaColumn(c) || c.uda.driver || truthy(c.hidden)) continue
    const g = String(c.group_name || 'Others')
    if (!baseGroups.has(g) && !out.includes(g)) out.push(g)
  }
  return out
}

/** Words that already say "master" at the end of a derived name (a table
 *  ITEM_MASTER / CUST_MST, a heading "Customer master"). The caption adds
 *  "master(s)" itself, so one of these is dropped first — never "item master
 *  masters". Data, not a special case: extend the list, not the code. */
export const MASTER_SUFFIX_WORDS: readonly string[] = ['master', 'masters', 'mst', 'mstr']

/** `name` without one trailing master word; a name that IS only that word is
 *  kept as it is (it is all there is to call the master). */
export function stripMasterSuffix(name: string): string {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (words.length > 1 && MASTER_SUFFIX_WORDS.includes(words[words.length - 1].toLowerCase())) {
    words.pop()
  }
  return words.join(' ')
}

/** The name of the master a lookup column reads: its heading ("Customer"),
 *  else the lookup table (`CUSTOMER(CUST_NAME, CUSTOMER_CODE)` → "customer"),
 *  without a trailing "master" word (`ITEM_MASTER` → "item"). */
export function masterNameOf(col: ColumnDef | null | undefined): string {
  if (!col) return ''
  const heading = String(col.heading || '').trim()
  if (heading) return stripMasterSuffix(heading.toLowerCase())
  const table = String(col.lookup || '').split('(')[0].trim()
  return stripMasterSuffix(table.replace(/_/g, ' ').toLowerCase())
}

/** "(from the customer master)" for a reason naming deciding values read
 *  from masters: each master-kind deciding value whose label the reason
 *  names, by the master its lookup column (`from.via`) reads. `columnOf`
 *  finds a `form.<col>` / `header.<col>` column. Empty when none applies. */
export function masterSourceNote(
  why: string, columns: readonly UdaColumnDef[] | null | undefined,
  columnOf: (via: string) => ColumnDef | null | undefined,
): string {
  const names: string[] = []
  for (const c of columns || []) {
    if (!isUdaColumn(c) || !c.uda.driver) continue
    const from = c.uda.from
    if (!from || from.kind !== 'master') continue
    const label = String(c.heading || c.uda.key || '')
    if (!label || !why.includes(label)) continue
    const name = masterNameOf(columnOf(from.via))
    if (name && !names.includes(name)) names.push(name)
  }
  if (!names.length) return ''
  const list = names.length === 1 ? names[0]
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  return `(from the ${list} master${names.length > 1 ? 's' : ''})`
}

/** The swatch colour an attribute's `pictures` give a stored value. */
export function swatchOf(col: ColumnDef, value: unknown): string | null {
  const code = value === null || value === undefined ? '' : String(value).trim()
  if (!code) return null
  return col.pictures?.by_value?.[code]?.swatch || null
}

/** Whether a promoted cell shows the red "Missing" label: the attribute
 *  applies to the row, is required and holds nothing. */
export function cellMissing(col: ColumnDef, value: unknown, attrKeys: readonly string[] | null | undefined): boolean {
  if (!isUdaColumn(col) || col.uda.driver || !truthy(col.mandatory)) return false
  if (!attrKeys || !attrKeys.includes(String(col.uda.key))) return false
  return blank(value)
}

